/**
 * Documentation - AI-SDR Agent Orchestration Documentation
 * 
 * Comprehensive documentation showing how the AI-SDR system works
 * via a system of AI agents for the ReWorld team.
 */

import React, { useContext, useState, useEffect } from "react";
import { useParams, useNavigate, NavLink } from "react-router-dom";
import {
  FaBook, FaRobot, FaPhone, FaEnvelope, FaCog,
  FaChartLine, FaCode, FaCheckCircle, FaLightbulb, FaShieldAlt,
  FaArrowLeft, FaArrowRight, FaCloud, FaServer, FaUsers,
  FaSearch, FaLinkedin, FaMapMarkerAlt, FaDatabase, FaNetworkWired,
  FaProjectDiagram, FaBrain, FaUserTie, FaBuilding, FaRoute
} from "react-icons/fa";
import { HiSparkles } from "react-icons/hi2";

// Mermaid diagram component
const MermaidDiagram = ({ chart, isDark }) => {
  const [svgContent, setSvgContent] = useState("");
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderMermaid = async () => {
      try {
        const mermaid = (await import("mermaid")).default;
        mermaid.initialize({
          startOnLoad: false,
          theme: isDark ? "dark" : "default",
          themeVariables: isDark
            ? {
                primaryColor: "#6366f1",
                primaryTextColor: "#fff",
                primaryBorderColor: "#4f46e5",
                lineColor: "#64748b",
                secondaryColor: "#1e293b",
                tertiaryColor: "#0f172a",
                background: "#1e293b",
                mainBkg: "#1e293b",
                nodeBorder: "#475569",
                clusterBkg: "#1e293b",
                titleColor: "#f1f5f9",
                edgeLabelBackground: "#1e293b",
              }
            : {
                primaryColor: "#6366f1",
                primaryTextColor: "#1e293b",
                primaryBorderColor: "#4f46e5",
                lineColor: "#64748b",
                secondaryColor: "#e0e7ff",
                tertiaryColor: "#f8fafc",
                background: "#ffffff",
                mainBkg: "#f1f5f9",
                nodeBorder: "#c7d2fe",
                clusterBkg: "#f8fafc",
                titleColor: "#1e293b",
                edgeLabelBackground: "#ffffff",
              },
          flowchart: {
            curve: "basis",
            padding: 20,
          },
        });

        const uniqueId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;
        const { svg } = await mermaid.render(uniqueId, chart);
        setSvgContent(svg);
      } catch (err) {
        console.error("Mermaid rendering error:", err);
        setError(err.message);
      }
    };

    renderMermaid();
  }, [chart, isDark]);

  if (error) {
    return (
      <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
        Diagram rendering error: {error}
      </div>
    );
  }

  return (
    <div
      className="mermaid-container overflow-auto"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

const Documentation = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const isDark = false; // AI-SDR Docs uses light theme only

  const sections = [
    { id: "overview", label: "Overview", icon: FaBook },
    { id: "agent-system", label: "Agent System", icon: FaRobot },
    { id: "company-research", label: "Company Research Agent", icon: FaBuilding },
    { id: "lead-research", label: "Lead Research Agent", icon: FaUsers },
    { id: "data-enrichment", label: "Data Enrichment Agents", icon: FaDatabase },
    { id: "email-agent", label: "Email Agent", icon: FaEnvelope },
    { id: "telephone-agent", label: "Telephone Agent", icon: FaPhone },
    { id: "campaign-orchestration", label: "Campaign Orchestration", icon: FaNetworkWired },
    { id: "data-flow", label: "Data Flow", icon: FaRoute },
    { id: "gcp-infrastructure", label: "GCP Infrastructure", icon: FaCloud },
  ];

  const currentSection = section || "overview";
  const currentIndex = sections.findIndex(s => s.id === currentSection);

  const renderContent = () => {
    switch (currentSection) {
      case "overview":
        return <OverviewSection isDark={isDark} />;
      case "agent-system":
        return <AgentSystemSection isDark={isDark} />;
      case "company-research":
        return <CompanyResearchSection isDark={isDark} />;
      case "lead-research":
        return <LeadResearchSection isDark={isDark} />;
      case "data-enrichment":
        return <DataEnrichmentSection isDark={isDark} />;
      case "email-agent":
        return <EmailAgentSection isDark={isDark} />;
      case "telephone-agent":
        return <TelephoneAgentSection isDark={isDark} />;
      case "campaign-orchestration":
        return <CampaignOrchestrationSection isDark={isDark} />;
      case "data-flow":
        return <DataFlowSection isDark={isDark} />;
      case "gcp-infrastructure":
        return <GCPInfrastructureSection isDark={isDark} />;
      default:
        return <OverviewSection isDark={isDark} />;
    }
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-slate-900 text-white" : "bg-white text-gray-900"}`}>
      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className={`w-64 flex-shrink-0 p-4 border-r min-h-screen sticky top-0 ${
          isDark ? "bg-slate-900/50 border-slate-700" : "bg-gray-50 border-gray-200"
        }`}>
          <h2 className={`font-bold text-lg mb-4 px-3 ${isDark ? "text-white" : "text-gray-900"}`}>
            AI-SDR Documentation
          </h2>
          <p className={`text-xs px-3 mb-4 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            ReWorld Team Agent System
          </p>
          <nav className="space-y-1">
            {sections.map((sec) => (
              <NavLink
                key={sec.id}
                to={`/ai-sdr/docs/${sec.id}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  currentSection === sec.id
                    ? isDark 
                      ? "bg-indigo-500/20 text-indigo-400 border border-indigo-500/30" 
                      : "bg-indigo-50 text-indigo-600 border border-indigo-200"
                    : isDark
                      ? "text-slate-400 hover:text-white hover:bg-slate-800/50"
                      : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                }`}
              >
                <sec.icon className="text-sm" />
                {sec.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-8 max-w-5xl">
          {renderContent()}

          {/* Navigation */}
          <div className={`mt-12 pt-6 border-t flex justify-between ${
            isDark ? "border-slate-700" : "border-gray-200"
          }`}>
            {currentIndex > 0 ? (
              <button
                onClick={() => navigate(`/ai-sdr/docs/${sections[currentIndex - 1].id}`)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"
                }`}
              >
                <FaArrowLeft className="text-sm" />
                {sections[currentIndex - 1].label}
              </button>
            ) : <div />}
            {currentIndex < sections.length - 1 && (
              <button
                onClick={() => navigate(`/ai-sdr/docs/${sections[currentIndex + 1].id}`)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  isDark ? "hover:bg-slate-800" : "hover:bg-gray-100"
                }`}
              >
                {sections[currentIndex + 1].label}
                <FaArrowRight className="text-sm" />
              </button>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

// ============================================================
// SECTION COMPONENTS
// ============================================================

const OverviewSection = ({ isDark }) => (
  <div>
    <h1 className="text-3xl font-bold mb-4">AI-SDR: Intelligent Sales Development Platform</h1>
    <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
      A comprehensive AI-powered Sales Development Representative system that uses a network of specialized 
      AI agents to automate prospecting, research, and outreach for the ReWorld team.
    </p>

    <div className={`p-6 rounded-2xl mb-6 ${isDark ? "bg-indigo-500/10 border border-indigo-500/30" : "bg-indigo-50 border border-indigo-200"}`}>
      <h3 className={`font-semibold mb-2 flex items-center gap-2 ${isDark ? "text-indigo-400" : "text-indigo-700"}`}>
        <HiSparkles className="text-xl" /> What This System Does
      </h3>
      <p className={isDark ? "text-slate-300" : "text-gray-600"}>
        The AI-SDR platform orchestrates multiple AI agents to identify, research, and engage potential 
        customers in the waste management and recycling industry. The goal is to achieve <strong>5 email 
        outreaches</strong> and <strong>3 phone outreaches</strong> per lead before moving on, automatically 
        stopping if a meeting is booked.
      </p>
    </div>

    <h2 className="text-xl font-semibold mb-3">Agent Ecosystem</h2>
    <div className="grid grid-cols-2 gap-4 mb-8">
      {[
        { title: "Company Research Agent", desc: "Discovers and analyzes target companies", icon: FaBuilding },
        { title: "Lead Research Agent", desc: "Identifies decision-makers within companies", icon: FaUsers },
        { title: "LinkedIn Sales Navigator Agent", desc: "Enriches lead data via LinkedIn", icon: FaLinkedin },
        { title: "Google Maps API Agent", desc: "Location and proximity analysis", icon: FaMapMarkerAlt },
        { title: "Apollo.io API Agent", desc: "Lead enrichment and contact data", icon: FaDatabase },
        { title: "Google Search Agent", desc: "Web intelligence gathering", icon: FaSearch },
        { title: "Email Agent", desc: "Personalized email sequence generation", icon: FaEnvelope },
        { title: "Telephone Agent", desc: "Voice outreach and voicemail", icon: FaPhone },
      ].map((agent) => (
        <div key={agent.title} className={`p-4 rounded-xl flex gap-4 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? "bg-indigo-500/20" : "bg-indigo-100"}`}>
            <agent.icon className={`${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
          </div>
          <div>
            <h4 className={`font-medium mb-1 ${isDark ? "text-white" : "text-gray-900"}`}>{agent.title}</h4>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{agent.desc}</p>
          </div>
        </div>
      ))}
    </div>

    <h2 className="text-xl font-semibold mb-3">Campaign Goals</h2>
    <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
      <ul className={`space-y-2 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        <li className="flex items-center gap-2">
          <FaCheckCircle className="text-emerald-500" /> 
          <span><strong>5 Email Outreaches</strong> per lead (Day 0, 3, 6, 10, 14)</span>
        </li>
        <li className="flex items-center gap-2">
          <FaCheckCircle className="text-emerald-500" /> 
          <span><strong>3 Phone Outreaches</strong> per lead (Voice + Voicemail)</span>
        </li>
        <li className="flex items-center gap-2">
          <FaCheckCircle className="text-emerald-500" /> 
          <span><strong>Auto-stop</strong> campaign if meeting is booked</span>
        </li>
        <li className="flex items-center gap-2">
          <FaCheckCircle className="text-emerald-500" /> 
          <span><strong>Move on</strong> after all touchpoints exhausted</span>
        </li>
      </ul>
    </div>

    <h2 className="text-xl font-semibold mb-3 mt-8">ReWorld Team</h2>
    <div className={`p-4 rounded-xl ${isDark ? "bg-green-500/10 border border-green-500/30" : "bg-green-50 border border-green-200"}`}>
      <p className={`text-sm font-mono ${isDark ? "text-green-400" : "text-green-700"}`}>
        Team ID: zTS5IWYUGpICLRKioMPm
      </p>
      <p className={`mt-2 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        ReWorld is a leading waste management company. This AI-SDR system helps their sales team 
        identify and engage with potential waste generator customers (manufacturing plants, distribution 
        centers, retailers) and waste collectors/processors.
      </p>
    </div>
  </div>
);

const AgentSystemSection = ({ isDark }) => {
  const agentFlowDiagram = `
flowchart TB
    subgraph Supervisor["Supervisor Agent"]
        SO[Orchestration Controller]
    end
    
    subgraph Research["Research Agents"]
        CR[Company Research Agent]
        LR[Lead Research Agent]
    end
    
    subgraph Enrichment["Enrichment Agents"]
        LN[LinkedIn Sales Navigator Agent]
        GM[Google Maps API Agent]
        AP[Apollo.io API Agent]
        GS[Google Search Agent]
    end
    
    subgraph Outreach["Outreach Agents"]
        EA[Email Agent]
        TA[Telephone Agent]
    end
    
    subgraph Data["Data Store"]
        FS[(Firestore)]
    end
    
    SO --> CR
    SO --> LR
    CR --> GM
    CR --> GS
    LR --> LN
    LR --> AP
    CR --> FS
    LR --> FS
    SO --> EA
    SO --> TA
    EA --> FS
    TA --> FS
    FS --> SO
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Agent System Architecture</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The AI-SDR operates through a hierarchical system of specialized agents, each responsible 
        for a specific task in the sales development workflow.
      </p>

      <h2 className="text-xl font-semibold mb-3">Agent Hierarchy</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={agentFlowDiagram} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Agent Responsibilities</h2>
      <div className="space-y-4">
        {[
          { 
            name: "Supervisor Agent", 
            role: "Orchestration Controller",
            desc: "Coordinates all other agents, manages campaign state, decides when to trigger outreach, and monitors goal completion.",
            color: "indigo"
          },
          { 
            name: "Company Research Agent", 
            role: "Company Discovery & Analysis",
            desc: "Identifies target companies using Google Maps proximity search, web scraping, and industry databases. Collects company size, revenue, location, and waste profile.",
            color: "blue"
          },
          { 
            name: "Lead Research Agent", 
            role: "Decision-Maker Identification",
            desc: "Finds key contacts within target companies: Sustainability Directors, Facility Managers, Operations VPs, and Procurement leads.",
            color: "purple"
          },
          { 
            name: "Data Enrichment Agents", 
            role: "Contact & Company Enrichment",
            desc: "LinkedIn, Apollo.io, and Google Search agents work together to enrich leads with emails, phone numbers, job history, and social profiles.",
            color: "cyan"
          },
          { 
            name: "Email Agent", 
            role: "Email Sequence Generation",
            desc: "Creates personalized 5-touch email sequences using AI, tailored to the prospect's role, industry, and pain points.",
            color: "green"
          },
          { 
            name: "Telephone Agent", 
            role: "Voice Outreach & Voicemail",
            desc: "Initiates calls via Twilio, generates voicemail scripts, and tracks call outcomes. Supports real-time voice AI for live conversations.",
            color: "amber"
          },
        ].map((agent) => (
          <div key={agent.name} className={`p-5 rounded-2xl border ${
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow"
          }`}>
            <div className="flex items-start gap-4">
              <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                agent.color === "indigo" ? "bg-indigo-500/20 text-indigo-400" :
                agent.color === "blue" ? "bg-blue-500/20 text-blue-400" :
                agent.color === "purple" ? "bg-purple-500/20 text-purple-400" :
                agent.color === "cyan" ? "bg-cyan-500/20 text-cyan-400" :
                agent.color === "green" ? "bg-green-500/20 text-green-400" :
                "bg-amber-500/20 text-amber-400"
              }`}>
                {agent.role}
              </div>
            </div>
            <h3 className={`font-semibold mt-3 ${isDark ? "text-white" : "text-gray-900"}`}>{agent.name}</h3>
            <p className={`mt-2 text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{agent.desc}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const CompanyResearchSection = ({ isDark }) => {
  const companyResearchFlow = `
sequenceDiagram
    participant U as User/Supervisor
    participant CR as Company Research Agent
    participant GM as Google Maps API
    participant GS as Google Search
    participant FS as Firestore

    U->>CR: Request nearby companies
    CR->>GM: Proximity search (lat/lng, radius)
    GM-->>CR: Company list with locations
    
    loop For each company
        CR->>GS: Search company info
        GS-->>CR: Website, revenue, employee count
        CR->>FS: Store enriched company
    end
    
    CR-->>U: Companies discovered
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Company Research Agent</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The Company Research Agent discovers potential customers by analyzing geographic proximity, 
        industry classification, and company characteristics.
      </p>

      <h2 className="text-xl font-semibold mb-3">Research Flow</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={companyResearchFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Data Sources</h2>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { 
            source: "Google Maps API", 
            data: "Business name, address, phone, website, hours, ratings, place ID",
            icon: FaMapMarkerAlt
          },
          { 
            source: "Google Search", 
            data: "Company website, LinkedIn page, news articles, industry info",
            icon: FaSearch
          },
          { 
            source: "Collector Database", 
            data: "Existing relationships, service areas, waste types handled",
            icon: FaDatabase
          },
          { 
            source: "Generator Database", 
            data: "Waste generators, facility types, estimated volumes",
            icon: FaBuilding
          },
        ].map((item) => (
          <div key={item.source} className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex items-center gap-2 mb-2">
              <item.icon className={`${isDark ? "text-indigo-400" : "text-indigo-600"}`} />
              <h4 className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{item.source}</h4>
            </div>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{item.data}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Company Data Model</h2>
      <div className={`p-4 rounded-xl font-mono text-sm overflow-x-auto ${isDark ? "bg-slate-900 text-slate-300" : "bg-gray-900 text-gray-300"}`}>
        <pre>{`{
  "id": "company_abc123",
  "name": "Acme Manufacturing",
  "address": "123 Industrial Blvd, Chicago, IL 60601",
  "lat": 41.8781,
  "lng": -87.6298,
  "phone": "+1-312-555-0100",
  "website": "https://acme-mfg.com",
  "industry": "Manufacturing",
  "employeeCount": "500-1000",
  "estimatedRevenue": "$50M-$100M",
  "wasteTypes": ["Industrial", "Recyclables", "Hazardous"],
  "distanceFromCollector": 15.3,  // miles
  "researchedAt": "2024-01-15T10:30:00Z",
  "enrichmentStatus": "complete"
}`}</pre>
      </div>

      <h2 className="text-xl font-semibold mb-3 mt-8">Proximity Search</h2>
      <p className={`mb-4 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The agent uses the Haversine formula to calculate distances and find companies within a specified radius:
      </p>
      <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <ul className={`space-y-2 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          <li>• <strong>Default radius:</strong> 100 miles from collector location</li>
          <li>• <strong>Industry keywords:</strong> "waste generator", "manufacturing", "distribution center"</li>
          <li>• <strong>Deduplication:</strong> Excludes companies already in the database</li>
          <li>• <strong>State-wide search:</strong> Option to search entire state by keyword</li>
        </ul>
      </div>
    </div>
  );
};

const LeadResearchSection = ({ isDark }) => {
  const leadResearchFlow = `
flowchart LR
    subgraph Input["Company Input"]
        C[Target Company]
    end
    
    subgraph Research["Lead Research Agent"]
        LI[LinkedIn Search]
        AP[Apollo.io Lookup]
        WS[Website Scrape]
    end
    
    subgraph Output["Lead Output"]
        L1[Sustainability Director]
        L2[Facility Manager]
        L3[VP Operations]
        L4[Procurement Lead]
    end
    
    C --> LI
    C --> AP
    C --> WS
    LI --> L1
    LI --> L2
    AP --> L3
    WS --> L4
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Lead Research Agent</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The Lead Research Agent identifies decision-makers within target companies who are most 
        likely to be involved in waste management decisions.
      </p>

      <h2 className="text-xl font-semibold mb-3">Lead Discovery Flow</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={leadResearchFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Target Personas</h2>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { title: "Sustainability Director", priority: "High", reason: "Owns environmental initiatives and waste reduction goals" },
          { title: "Facility Manager", priority: "High", reason: "Controls day-to-day waste operations and vendor selection" },
          { title: "VP Operations", priority: "Medium", reason: "Budget authority for operational contracts" },
          { title: "Procurement Lead", priority: "Medium", reason: "Manages vendor relationships and RFP processes" },
          { title: "EHS Manager", priority: "Medium", reason: "Environmental Health & Safety compliance oversight" },
          { title: "CFO/Controller", priority: "Low", reason: "Final approval for large contracts" },
        ].map((persona) => (
          <div key={persona.title} className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{persona.title}</h4>
              <span className={`text-xs px-2 py-1 rounded-full ${
                persona.priority === "High" ? "bg-red-500/20 text-red-400" :
                persona.priority === "Medium" ? "bg-amber-500/20 text-amber-400" :
                "bg-slate-500/20 text-slate-400"
              }`}>{persona.priority}</span>
            </div>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{persona.reason}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Lead Data Model</h2>
      <div className={`p-4 rounded-xl font-mono text-sm overflow-x-auto ${isDark ? "bg-slate-900 text-slate-300" : "bg-gray-900 text-gray-300"}`}>
        <pre>{`{
  "id": "lead_xyz789",
  "fullName": "Jane Smith",
  "email": "jane.smith@acme-mfg.com",
  "phone": "+1-312-555-0101",
  "title": "Director of Sustainability",
  "company": "Acme Manufacturing",
  "companyId": "company_abc123",
  "linkedinUrl": "https://linkedin.com/in/janesmith",
  "industry": "Manufacturing",
  "location": "Chicago, IL",
  
  // Email sequence status
  "day1-problem-solution": "...",
  "day4-case-study": "...",
  "day7-role-value": "...",
  "day10-urgency": "...",
  "day14-breakup": "...",
  
  // Outreach tracking
  "emailsSent": 2,
  "callsMade": 1,
  "lastContactDate": "2024-01-20T14:00:00Z",
  "status": "in_progress"
}`}</pre>
      </div>
    </div>
  );
};

const DataEnrichmentSection = ({ isDark }) => {
  const enrichmentFlow = `
flowchart TB
    subgraph Input["Raw Lead"]
        RL[Name + Company Only]
    end
    
    subgraph Enrichment["Enrichment Agents"]
        direction TB
        LN[LinkedIn Agent]
        AP[Apollo.io Agent]
        GS[Google Search Agent]
        GM[Google Maps Agent]
    end
    
    subgraph Output["Enriched Lead"]
        direction TB
        E[Email]
        P[Phone]
        T[Title]
        H[Job History]
        S[Social Profiles]
        L[Location]
    end
    
    RL --> LN
    RL --> AP
    RL --> GS
    RL --> GM
    
    LN --> E
    LN --> T
    LN --> H
    LN --> S
    AP --> E
    AP --> P
    AP --> T
    GS --> S
    GM --> L
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Data Enrichment Agents</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        Multiple specialized agents work together to enrich lead data with contact information, 
        professional history, and social profiles.
      </p>

      <h2 className="text-xl font-semibold mb-3">Enrichment Pipeline</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={enrichmentFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Enrichment Sources</h2>
      <div className="space-y-4 mb-8">
        {[
          {
            name: "LinkedIn Sales Navigator Agent",
            icon: FaLinkedin,
            color: "blue",
            capabilities: [
              "Search by name + company",
              "Extract current job title",
              "Get work history",
              "Find mutual connections",
              "Identify decision-making authority"
            ]
          },
          {
            name: "Apollo.io API Agent",
            icon: FaDatabase,
            color: "purple",
            capabilities: [
              "Verified email addresses",
              "Direct phone numbers",
              "Company technographics",
              "Intent signals",
              "Buyer seniority scoring"
            ]
          },
          {
            name: "Google Search Agent",
            icon: FaSearch,
            color: "amber",
            capabilities: [
              "Press releases and news",
              "Conference speaker appearances",
              "Published articles",
              "Company announcements",
              "Industry involvement"
            ]
          },
          {
            name: "Google Maps API Agent",
            icon: FaMapMarkerAlt,
            color: "red",
            capabilities: [
              "Facility locations",
              "Multiple site detection",
              "Operating hours",
              "Business verification",
              "Proximity calculations"
            ]
          },
        ].map((agent) => (
          <div key={agent.name} className={`p-5 rounded-2xl border ${
            isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow"
          }`}>
            <div className="flex items-center gap-3 mb-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                agent.color === "blue" ? "bg-blue-500/20" :
                agent.color === "purple" ? "bg-purple-500/20" :
                agent.color === "amber" ? "bg-amber-500/20" :
                "bg-red-500/20"
              }`}>
                <agent.icon className={`${
                  agent.color === "blue" ? "text-blue-400" :
                  agent.color === "purple" ? "text-purple-400" :
                  agent.color === "amber" ? "text-amber-400" :
                  "text-red-400"
                }`} />
              </div>
              <h3 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{agent.name}</h3>
            </div>
            <ul className={`grid grid-cols-2 gap-2 text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>
              {agent.capabilities.map((cap, i) => (
                <li key={i} className="flex items-center gap-2">
                  <FaCheckCircle className="text-emerald-500 text-xs" />
                  {cap}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Enrichment Quality Scores</h2>
      <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <table className={`w-full text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          <thead className={isDark ? "bg-slate-700" : "bg-gray-200"}>
            <tr>
              <th className="px-4 py-2 text-left">Confidence</th>
              <th className="px-4 py-2 text-left">Criteria</th>
              <th className="px-4 py-2 text-left">Action</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDark ? "divide-slate-700" : "divide-gray-200"}`}>
            <tr>
              <td className="px-4 py-2"><span className="px-2 py-1 rounded bg-green-500/20 text-green-400">High</span></td>
              <td className="px-4 py-2">Email + Phone verified, LinkedIn matched</td>
              <td className="px-4 py-2">Ready for outreach</td>
            </tr>
            <tr>
              <td className="px-4 py-2"><span className="px-2 py-1 rounded bg-amber-500/20 text-amber-400">Medium</span></td>
              <td className="px-4 py-2">Email verified, no phone</td>
              <td className="px-4 py-2">Email outreach only</td>
            </tr>
            <tr>
              <td className="px-4 py-2"><span className="px-2 py-1 rounded bg-red-500/20 text-red-400">Low</span></td>
              <td className="px-4 py-2">Unverified or missing data</td>
              <td className="px-4 py-2">Re-enrich or skip</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

const EmailAgentSection = ({ isDark }) => {
  const emailSequenceFlow = `
sequenceDiagram
    participant S as Supervisor
    participant EA as Email Agent
    participant AI as OpenAI/Gemini
    participant FS as Firestore
    participant ES as Email Service

    S->>EA: Generate sequence for lead
    EA->>FS: Get lead + company data
    FS-->>EA: Lead profile
    
    loop For each touchpoint
        EA->>AI: Generate personalized email
        AI-->>EA: Subject + Body
        EA->>FS: Store template
    end
    
    EA-->>S: Sequence ready
    
    Note over S,ES: Campaign Execution
    S->>EA: Send Day 0 email
    EA->>ES: Deliver email
    ES-->>EA: Delivered/Bounced
    EA->>FS: Update status
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Email Agent</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The Email Agent generates personalized, multi-touch email sequences using AI, designed 
        to progressively build interest and drive meeting bookings.
      </p>

      <h2 className="text-xl font-semibold mb-3">Email Sequence Flow</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={emailSequenceFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">5-Touch Email Cadence</h2>
      <div className="space-y-4 mb-8">
        {[
          { day: "Day 0", name: "Spark", goal: "Introduce and highlight one key result", cta: "Quick chat suggestion", subject: "Cut Landfill Fees 30%? Quick intro from ReWorld" },
          { day: "Day 3", name: "Value Add", goal: "Share mini case study and video link", cta: "Offer calculator/tool", subject: "60-sec video: How [peer] hit zero-waste targets" },
          { day: "Day 6", name: "Pain/Insight", goal: "Highlight hidden costs, ask engaging question", cta: "Offer breakdown/analysis", subject: "Hidden cost on your waste invoice?" },
          { day: "Day 10", name: "Social Proof", goal: "Share quantified peer outcome", cta: "Meeting calendar link", subject: "[Peer] saved $X in hauling—see the numbers?" },
          { day: "Day 14", name: "Break-up", goal: "Friendly final outreach with easy out", cta: "Closing the loop", subject: "Should I close the file?" },
        ].map((touch, i) => (
          <div key={touch.day} className={`p-4 rounded-xl border-l-4 ${
            i === 0 ? "border-green-500" :
            i === 1 ? "border-blue-500" :
            i === 2 ? "border-amber-500" :
            i === 3 ? "border-purple-500" :
            "border-red-500"
          } ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <span className={`text-sm font-bold ${isDark ? "text-indigo-400" : "text-indigo-600"}`}>{touch.day}</span>
                <span className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{touch.name}</span>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>{touch.cta}</span>
            </div>
            <p className={`text-sm mb-2 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{touch.goal}</p>
            <p className={`text-xs font-mono ${isDark ? "text-slate-500" : "text-gray-400"}`}>Subject: "{touch.subject}"</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Personalization Variables</h2>
      <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? "bg-slate-900 text-slate-300" : "bg-gray-900 text-gray-300"}`}>
        <pre>{`RECIPIENT INFO:
- Role: {lead.title}
- Industry: {company.industry}
- Company: {company.name}
- Pain Points: {industry.commonChallenges}

SENDER INFO:
- Name: {persona.name}
- Role: {persona.role}
- Company: ReWorld

CONTEXT:
- Previous Emails: {campaign.emailHistory}
- Engagement: {lead.openRate}, {lead.clickRate}
- Competitor Intel: {company.currentVendor}`}</pre>
      </div>

      <h2 className="text-xl font-semibold mb-3 mt-8">Stop Conditions</h2>
      <div className={`p-4 rounded-xl ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
        <ul className={`space-y-2 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          <li className="flex items-center gap-2">
            <FaCheckCircle className="text-emerald-500" />
            <span><strong>Meeting Booked:</strong> Calendar invite confirmed</span>
          </li>
          <li className="flex items-center gap-2">
            <FaCheckCircle className="text-emerald-500" />
            <span><strong>Reply Received:</strong> Any response triggers pause for human review</span>
          </li>
          <li className="flex items-center gap-2">
            <FaCheckCircle className="text-emerald-500" />
            <span><strong>Unsubscribe:</strong> Immediate removal from sequence</span>
          </li>
          <li className="flex items-center gap-2">
            <FaCheckCircle className="text-emerald-500" />
            <span><strong>Bounce:</strong> Email invalid, mark lead for re-enrichment</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

const TelephoneAgentSection = ({ isDark }) => {
  const telephoneFlow = `
stateDiagram-v2
    [*] --> Initiated: Start Call
    Initiated --> Connecting: Twilio Dial
    Connecting --> IVR: Phone Tree Detected
    Connecting --> Human: Human Answers
    Connecting --> Voicemail: Voicemail Detected
    Connecting --> Failed: No Answer/Busy
    
    IVR --> Human: Navigate to HR
    IVR --> Failed: Stuck in Loop
    
    Human --> Script: Read Script
    Script --> Completed: Conversation Done
    
    Voicemail --> LeaveMsg: Play Message
    LeaveMsg --> Completed: Message Left
    
    Failed --> [*]
    Completed --> [*]
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Telephone Agent</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The Telephone Agent handles voice outreach via Twilio, supporting both voicemail drops 
        and real-time AI voice conversations.
      </p>

      <h2 className="text-xl font-semibold mb-3">Call State Machine</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={telephoneFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Call Modes</h2>
      <div className="grid grid-cols-2 gap-4 mb-8">
        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
          <h4 className={`font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>Voicemail Mode</h4>
          <ul className={`text-sm space-y-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            <li>• Pre-recorded AI voicemail script</li>
            <li>• Automatic voicemail detection</li>
            <li>• Callback number included</li>
            <li>• 30-second max duration</li>
          </ul>
        </div>
        <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
          <h4 className={`font-semibold mb-2 ${isDark ? "text-white" : "text-gray-900"}`}>Real-Time Voice</h4>
          <ul className={`text-sm space-y-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
            <li>• OpenAI real-time speech API</li>
            <li>• Live conversation handling</li>
            <li>• Dynamic script adaptation</li>
            <li>• Transcription logging</li>
          </ul>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-3">Voicemail Script Template</h2>
      <div className={`p-4 rounded-xl border-l-4 border-purple-500 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <p className={`italic ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          "Hi {"{lead.firstName}"}, this is {"{persona.name}"} from ReWorld. I'm reaching out because 
          we've helped companies like yours reduce waste costs by up to 30%. I'd love to share how 
          we might do the same for {"{company.name}"}. You can reach me at {"{callback.number}"} or 
          reply to my email. Looking forward to connecting!"
        </p>
      </div>

      <h2 className="text-xl font-semibold mb-3 mt-8">Call Outcomes</h2>
      <div className="space-y-3">
        {[
          { status: "CONNECTED", color: "emerald", desc: "Spoke with target or relevant contact" },
          { status: "VOICEMAIL", color: "purple", desc: "Left voicemail message" },
          { status: "NO_ANSWER", color: "amber", desc: "No answer after 30 seconds" },
          { status: "BUSY", color: "orange", desc: "Line busy, will retry" },
          { status: "WRONG_NUMBER", color: "red", desc: "Number incorrect, needs re-enrichment" },
          { status: "GATEKEEPER", color: "blue", desc: "Blocked by receptionist/assistant" },
        ].map((outcome) => (
          <div key={outcome.status} className={`flex items-center gap-4 p-3 rounded-lg ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <span className={`px-2 py-1 rounded text-sm font-semibold ${
              outcome.color === "emerald" ? "bg-emerald-500/20 text-emerald-400" :
              outcome.color === "purple" ? "bg-purple-500/20 text-purple-400" :
              outcome.color === "amber" ? "bg-amber-500/20 text-amber-400" :
              outcome.color === "orange" ? "bg-orange-500/20 text-orange-400" :
              outcome.color === "red" ? "bg-red-500/20 text-red-400" :
              "bg-blue-500/20 text-blue-400"
            }`}>{outcome.status}</span>
            <span className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{outcome.desc}</span>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3 mt-8">3-Call Strategy</h2>
      <div className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <ol className={`space-y-2 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          <li><strong>Call 1 (Day 2):</strong> After initial email, reference email and offer quick chat</li>
          <li><strong>Call 2 (Day 7):</strong> After 3rd email, share specific value prop</li>
          <li><strong>Call 3 (Day 12):</strong> Before break-up email, last attempt with urgency</li>
        </ol>
      </div>
    </div>
  );
};

const CampaignOrchestrationSection = ({ isDark }) => {
  const campaignFlow = `
flowchart TB
    subgraph Campaign["Campaign Lifecycle"]
        direction TB
        C1[Create Campaign]
        C2[Add Leads]
        C3[Generate Sequences]
        C4[Start Campaign]
        C5[Execute Touchpoints]
        C6[Monitor & Adjust]
        C7[Complete/Stop]
    end
    
    subgraph Touchpoints["Daily Touchpoints"]
        E1[Email Day 0]
        P1[Call Day 2]
        E2[Email Day 3]
        E3[Email Day 6]
        P2[Call Day 7]
        E4[Email Day 10]
        P3[Call Day 12]
        E5[Email Day 14]
    end
    
    subgraph Outcomes["Outcome Handling"]
        M[Meeting Booked]
        R[Reply Received]
        U[Unsubscribed]
        X[Exhausted]
    end
    
    C1 --> C2 --> C3 --> C4 --> C5
    C5 --> E1 --> P1 --> E2 --> E3 --> P2 --> E4 --> P3 --> E5
    C5 --> C6 --> C7
    
    E1 --> M
    P1 --> M
    E2 --> R
    E3 --> R
    E5 --> U
    E5 --> X
    
    M --> C7
    R --> C6
    U --> C7
    X --> C7
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Campaign Orchestration</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The Supervisor Agent orchestrates the entire campaign lifecycle, coordinating all agents 
        and ensuring optimal timing of touchpoints.
      </p>

      <h2 className="text-xl font-semibold mb-3">Campaign Lifecycle</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={campaignFlow} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Touchpoint Timeline</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <div className="relative">
          {/* Timeline line */}
          <div className={`absolute left-4 top-0 bottom-0 w-0.5 ${isDark ? "bg-slate-600" : "bg-gray-300"}`}></div>
          
          {/* Timeline items */}
          <div className="space-y-4 pl-12">
            {[
              { day: 0, type: "email", label: "Spark Email", color: "green" },
              { day: 2, type: "call", label: "Intro Call", color: "purple" },
              { day: 3, type: "email", label: "Value Add Email", color: "blue" },
              { day: 6, type: "email", label: "Pain/Insight Email", color: "amber" },
              { day: 7, type: "call", label: "Follow-up Call", color: "purple" },
              { day: 10, type: "email", label: "Social Proof Email", color: "cyan" },
              { day: 12, type: "call", label: "Urgency Call", color: "purple" },
              { day: 14, type: "email", label: "Break-up Email", color: "red" },
            ].map((item, i) => (
              <div key={i} className="relative flex items-center gap-4">
                <div className={`absolute -left-12 w-8 h-8 rounded-full flex items-center justify-center ${
                  item.type === "email" ? "bg-indigo-500/20" : "bg-purple-500/20"
                }`}>
                  {item.type === "email" ? 
                    <FaEnvelope className={`text-sm ${isDark ? "text-indigo-400" : "text-indigo-600"}`} /> :
                    <FaPhone className={`text-sm ${isDark ? "text-purple-400" : "text-purple-600"}`} />
                  }
                </div>
                <span className={`text-sm font-mono ${isDark ? "text-slate-500" : "text-gray-400"}`}>Day {item.day}</span>
                <span className={`text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <h2 className="text-xl font-semibold mb-3">Campaign States</h2>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { state: "Draft", desc: "Campaign created, not yet started" },
          { state: "Active", desc: "Campaign running, touchpoints executing" },
          { state: "Paused", desc: "Temporarily stopped, can resume" },
          { state: "Completed", desc: "All touchpoints exhausted or goal met" },
        ].map((item) => (
          <div key={item.state} className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{item.state}</h4>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{item.desc}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Success Criteria</h2>
      <div className={`p-4 rounded-xl ${isDark ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-emerald-50 border border-emerald-200"}`}>
        <h4 className={`font-semibold mb-2 ${isDark ? "text-emerald-400" : "text-emerald-700"}`}>
          Meeting Goal Achieved
        </h4>
        <p className={`text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          Campaign stops automatically when a meeting is booked. This is detected via:
        </p>
        <ul className={`mt-2 text-sm space-y-1 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
          <li>• Calendar invite sent/accepted</li>
          <li>• Reply with meeting confirmation</li>
          <li>• Manual status update by SDR</li>
        </ul>
      </div>
    </div>
  );
};

const DataFlowSection = ({ isDark }) => {
  const dataFlowDiagram = `
flowchart LR
    subgraph Sources["Data Sources"]
        GM[Google Maps]
        LI[LinkedIn]
        AP[Apollo.io]
        GS[Google Search]
    end
    
    subgraph Processing["Processing Layer"]
        CR[Company Research]
        LR[Lead Research]
        EN[Enrichment]
    end
    
    subgraph Storage["Firestore"]
        TC[/teams/]
        CO[/companies/]
        LE[/leads/]
        CA[/campaigns/]
        CT[/contacts/]
    end
    
    subgraph Outreach["Outreach"]
        EA[Email Service]
        TW[Twilio Voice]
    end
    
    GM --> CR
    LI --> LR
    AP --> EN
    GS --> CR
    GS --> LR
    
    CR --> CO
    LR --> LE
    EN --> CT
    
    CO --> CA
    LE --> CA
    CT --> CA
    
    CA --> EA
    CA --> TW
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">Data Flow Architecture</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        Understanding how data flows through the AI-SDR system from initial research to outreach execution.
      </p>

      <h2 className="text-xl font-semibold mb-3">End-to-End Data Flow</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={dataFlowDiagram} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">Firestore Collections</h2>
      <div className={`p-4 rounded-xl font-mono text-sm overflow-x-auto mb-8 ${isDark ? "bg-slate-900 text-slate-300" : "bg-gray-900 text-gray-300"}`}>
        <pre>{`/teams/{teamId}/
  ├── collectors/{collectorId}     // Waste collectors (ReWorld locations)
  ├── generators/{generatorId}     // Waste generators (target companies)
  └── research/{researchId}        // Enriched research data

/userContent/{userId}/aiSdr/collections/
  ├── campaigns/{campaignId}       // Campaign definitions
  │   └── templates/{templateId}   // Email templates per campaign
  ├── leads/{leadId}               // Individual leads
  ├── personas/{personaId}         // Sender personas
  └── companies/{companyId}        // Company records

/contacts/{contactId}               // Global contact records with emails
  ├── day1-problem-solution        // Email content
  ├── day4-case-study
  ├── day7-role-value
  ├── day10-urgency
  └── day14-breakup`}</pre>
      </div>

      <h2 className="text-xl font-semibold mb-3">API Endpoints</h2>
      <table className={`w-full text-sm ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        <thead className={isDark ? "bg-slate-800" : "bg-gray-100"}>
          <tr>
            <th className="px-4 py-2 text-left">Endpoint</th>
            <th className="px-4 py-2 text-left">Purpose</th>
            <th className="px-4 py-2 text-left">Agent</th>
          </tr>
        </thead>
        <tbody className={`divide-y ${isDark ? "divide-slate-700" : "divide-gray-200"}`}>
          <tr><td className="px-4 py-2 font-mono">/composeEmailTemplate</td><td className="px-4 py-2">Generate AI email</td><td className="px-4 py-2">Email Agent</td></tr>
          <tr><td className="px-4 py-2 font-mono">/initiateCall</td><td className="px-4 py-2">Start Twilio call</td><td className="px-4 py-2">Telephone Agent</td></tr>
          <tr><td className="px-4 py-2 font-mono">/getCallDetails</td><td className="px-4 py-2">Poll call status</td><td className="px-4 py-2">Telephone Agent</td></tr>
          <tr><td className="px-4 py-2 font-mono">/getTempSpeechTokenSDR</td><td className="px-4 py-2">OpenAI voice token</td><td className="px-4 py-2">Telephone Agent</td></tr>
          <tr><td className="px-4 py-2 font-mono">/similar</td><td className="px-4 py-2">Proximity search</td><td className="px-4 py-2">Company Research</td></tr>
          <tr><td className="px-4 py-2 font-mono">/collectorProximitySearch</td><td className="px-4 py-2">Collector nearby</td><td className="px-4 py-2">Company Research</td></tr>
        </tbody>
      </table>
    </div>
  );
};

const GCPInfrastructureSection = ({ isDark }) => {
  const gcpArchitecture = `
flowchart TB
    subgraph Client["Client Layer"]
        R[React App]
        FB[Firebase Hosting]
    end
    
    subgraph GCP["Google Cloud Platform"]
        CF[Cloud Functions]
        FS[(Firestore)]
        ST[(Cloud Storage)]
        AI[Vertex AI]
    end
    
    subgraph External["External Services"]
        TW[Twilio]
        OA[OpenAI]
        GM[Google Maps]
        AP[Apollo.io]
    end
    
    R --> FB
    FB --> CF
    CF --> FS
    CF --> ST
    CF --> AI
    CF --> TW
    CF --> OA
    CF --> GM
    CF --> AP
`;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-4">GCP Infrastructure</h1>
      <p className={`text-lg mb-6 ${isDark ? "text-slate-300" : "text-gray-600"}`}>
        The AI-SDR platform is built on Google Cloud Platform, leveraging Firebase services 
        for real-time data and Cloud Functions for backend processing.
      </p>

      <h2 className="text-xl font-semibold mb-3">Architecture Overview</h2>
      <div className={`p-4 rounded-xl mb-8 ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
        <MermaidDiagram chart={gcpArchitecture} isDark={isDark} />
      </div>

      <h2 className="text-xl font-semibold mb-3">GCP Services</h2>
      <div className="space-y-4 mb-8">
        {[
          {
            service: "Firebase Hosting",
            purpose: "Static React app hosting with global CDN",
            config: "firebase.json, .firebaserc"
          },
          {
            service: "Cloud Functions",
            purpose: "Serverless backend APIs (Node.js)",
            config: "functions/index.js"
          },
          {
            service: "Firestore",
            purpose: "NoSQL document database for all data",
            config: "firestore.rules"
          },
          {
            service: "Cloud Storage",
            purpose: "File storage for attachments, exports",
            config: "storage.rules"
          },
          {
            service: "Vertex AI",
            purpose: "AI model hosting (future)",
            config: "vertex-ai-config"
          },
        ].map((item) => (
          <div key={item.service} className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{item.service}</h4>
              <code className={`text-xs px-2 py-1 rounded ${isDark ? "bg-slate-700 text-slate-300" : "bg-gray-200 text-gray-600"}`}>{item.config}</code>
            </div>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{item.purpose}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">External Integrations</h2>
      <div className="grid grid-cols-2 gap-4 mb-8">
        {[
          { name: "Twilio", purpose: "Voice calls, SMS, voicemail", status: "Active" },
          { name: "OpenAI", purpose: "Email generation, voice AI", status: "Active" },
          { name: "Google Maps", purpose: "Proximity search, geocoding", status: "Active" },
          { name: "Apollo.io", purpose: "Lead enrichment", status: "Active" },
          { name: "LinkedIn", purpose: "Sales Navigator data", status: "Planned" },
          { name: "SendGrid", purpose: "Email delivery", status: "Planned" },
        ].map((item) => (
          <div key={item.name} className={`p-4 rounded-xl ${isDark ? "bg-slate-800/50" : "bg-gray-50"}`}>
            <div className="flex items-center justify-between mb-2">
              <h4 className={`font-semibold ${isDark ? "text-white" : "text-gray-900"}`}>{item.name}</h4>
              <span className={`text-xs px-2 py-1 rounded ${
                item.status === "Active" ? "bg-green-500/20 text-green-400" : "bg-amber-500/20 text-amber-400"
              }`}>{item.status}</span>
            </div>
            <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>{item.purpose}</p>
          </div>
        ))}
      </div>

      <h2 className="text-xl font-semibold mb-3">Deployment</h2>
      <div className={`p-4 rounded-xl font-mono text-sm ${isDark ? "bg-slate-900 text-slate-300" : "bg-gray-900 text-gray-300"}`}>
        <pre>{`# Deploy frontend
cd app && npm run build && firebase deploy --only hosting

# Deploy functions
cd functions && npm install && firebase deploy --only functions

# Deploy Firestore rules
firebase deploy --only firestore:rules

# Full deployment
./build-and-deploy.sh`}</pre>
      </div>

      <h2 className="text-xl font-semibold mb-3 mt-8">Environment Variables</h2>
      <div className={`p-4 rounded-xl ${isDark ? "bg-amber-500/10 border border-amber-500/30" : "bg-amber-50 border border-amber-200"}`}>
        <h4 className={`font-semibold mb-2 ${isDark ? "text-amber-400" : "text-amber-700"}`}>
          Required Environment Variables
        </h4>
        <ul className={`text-sm space-y-1 font-mono ${isDark ? "text-slate-300" : "text-gray-600"}`}>
          <li>REACT_APP_API_URL - Cloud Functions base URL</li>
          <li>REACT_APP_GOOGLE_MAPS_API_KEY - Maps API key</li>
          <li>TWILIO_ACCOUNT_SID - Twilio account</li>
          <li>TWILIO_AUTH_TOKEN - Twilio auth</li>
          <li>OPENAI_API_KEY - OpenAI key</li>
          <li>APOLLO_API_KEY - Apollo.io key</li>
        </ul>
      </div>
    </div>
  );
};

export default Documentation;
